import React, { useEffect, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Navigation, Pagination, Autoplay, Thumbs } from 'swiper';
import 'swiper/swiper-bundle.min.css';
import { Zoom, ImageMultiply, Icon3d, IconArrowDown } from '@/components/atoms';
import styled from 'styled-components';
import ReactImageMagnify from 'react-image-magnify';
import Image from 'next/image';

SwiperCore.use([Navigation, Pagination, Autoplay, Thumbs]);

const Container = styled.div`
  width: 100%;
  margin: auto;
  max-width: 688px;
  display: flex;

  position: relative;
  .swiper-wrapper {
    align-items: center;
  }
  ${props => props.theme.breakpoints.up('sm')} {
    max-width: 380px;
  }
  ${props => props.theme.breakpoints.up('md')} {
    max-width: 458px;
  }
  ${props => props.theme.breakpoints.up('laptop')} {
    max-width: 628px;
  }
  ${props => props.theme.breakpoints.up('lg')} {
    max-width: 700px;
  }
`;

const ImgBigContainer = styled.div`
  background-color: ${props => props.theme.productCard.backgroundColor};
  height: 450px;
  display: flex;
  align-items: center;
  justify-content: center;

  ${props => props.theme.breakpoints.up('sm')} {
    height: 380px;
  }

  ${props => props.theme.breakpoints.up('md')} {
    height: 450px;
  }

  ${props => props.theme.breakpoints.up('lg')} {
    height: 600px;
  }

  img {
    mix-blend-mode: multiply;
    max-width: none;

    &.imgstyle {
      object-fit: contain;
      max-height: 450px;

      ${props => props.theme.breakpoints.up('sm')} {
        max-height: 380px;
      }

      ${props => props.theme.breakpoints.up('md')} {
        max-height: 450px;
        width: 100%;
      }

      ${props => props.theme.breakpoints.up('lg')} {
        max-height: 600px;
      }
    }
  }
  .enContainer {
    z-index: 9999;
    background-color: ${props => props.theme.productCard.backgroundColor};
    img {
      mix-blend-mode: multiply;
      max-width: none;
    }
  }
`;

const SwiperContainer = styled.div`
  margin-left: 0px;
  width: 100%;
  position: relative;
  .threed {
    position: absolute;
    bottom: 48px;
    right: 24px;
    z-index: 9;
  }
  .swiper-pagination {
    display: block;
  }
  .swiper-pagination-bullet-active {
    background-color: ${props => props.theme.paletteDefault.primary.main};
  }
  ${props => props.theme.breakpoints.up('sm')} {
    max-width: 380px;
    margin-left: 0px;
  }
  ${props => props.theme.breakpoints.up('md')} {
    max-width: 450px;
    margin-left: 16px;
  }

  ${props => props.theme.breakpoints.up('lg')} {
    max-width: 500px;
    margin-left: 24px;
    .swiper-pagination {
      display: none;
    }
  }
  ${props => props.theme.breakpoints.up('xl')} {
    max-width: 600px;
  }
`;

const ThumbSwiperContainer = styled.div<IThumbSwiperContainer>`
	width: 100px;
	.swiper-wrapper {
		display: flex;
		flex-direction: column;
		max-width: 100px;
		height: 450px;
		width: 100px;

		${props => props.theme.breakpoints.up('sm')} {
			/* height: 380px; */
		}
		${props => props.theme.breakpoints.up('md')} {
			/* height: 450px; */

			display: none;
		}


		${props => props.theme.breakpoints.up('lg')} {
			height: 600px;

			display: block;
		}
	}

	display: none;
	${props => props.theme.breakpoints.up('lg')} {
		display: block;
	}
	.nav {
		position: absolute;
		transform: translate(-50%);
		left: 50%;
		z-index: 9;
		cursor: pointer;
		display: ${props => (props.showNav ? 'block' : 'none')};
	}
	.navPrev {
		top: 0;
	}
	.navNext {
		bottom: -6px;
	}

	.swiper-slide {
		background-color: ${props => props.theme.productCard.backgroundColor};
	}
	.swiper-slide:first-child {
		border: 1px solid ${props => props.theme.paletteDefault.grey1};
	}
	.swiper-slide-thumb-active {
		border: 1px solid ${props => props.theme.paletteDefault.grey1}; !important;
	}
`;

const ThumbContainer = styled.div`
  cursor: pointer;
  height: 100px;
  background-color: ${props => props.theme.productCard.backgroundColor};
`;

const IconsContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: absolute;
  right: 8px;
  top: 16px;
  z-index: 100;
  align-items: center;

  ${props => props.theme.breakpoints.up('laptop')} {
    right: 16px;
  }
`;
const IconContainer = styled.div`
  margin-bottom: 16px;
`;

const SliderWrapper = styled.div`
  position: relative;
  ${props => props.theme.breakpoints.up('md')} {
    position: sticky;
    top: 160px;
  }
`;

interface IThumbSwiperContainer {
  showNav: boolean;
}
interface IProductImage {
  width: number;
  height: number;
  src: string;
  title: string;
}

interface IProductSlider {
  slideCollection: {
    items: IProductImage[];
  };
  openFutureFashion: () => void;
  url: string;
  availability?: boolean | undefined;
  show3dicon: string | null;
}

const setThumbActiveStyle = (init: boolean) => {
  let color = init ? '#707070' : 'transparent';
  let slides = document.querySelectorAll(
    '.swiper-container-thumbs .swiper-slide'
  ) as NodeListOf<HTMLElement>;
  if (slides && slides.length > 0) {
    slides[0].style.border = `1px solid ${color}`;
  }
};

const ArrowWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${props => props.theme.paletteDefault.white};
  color: ${props => props.theme.paletteDefault.primary.main};
`;

const IconArrowUp = styled(IconArrowDown)`
  transform: rotate(180deg);
`;

const ProductSlider: React.FC<IProductSlider> = props => {
  const { slideCollection, url, openFutureFashion, show3dicon } = props;

  const [thumbsSwiper, setThumbsSwiper] = useState<null | any>(null);
  const [swiper, setSwiper] = useState<null | any>(null);

  useEffect(() => {
    if (swiper) {
      swiper.slideTo(0);
    }
    if (thumbsSwiper) {
      setThumbActiveStyle(true);
    }
  }, [url]);

  return (
    <SliderWrapper>
      <Container>
        <ThumbSwiperContainer
          showNav={slideCollection.items.length > 5 ? true : false}>
          <Swiper
            id="thumbs"
            onSwiper={setThumbsSwiper}
            spaceBetween={16}
            slidesPerView={5}
            direction="vertical"
            navigation={{
              nextEl: '.navNext',
              prevEl: '.navPrev',
            }}
            className="th-swiper"
            onClick={() => {
              setThumbActiveStyle(false);
            }}>
            {slideCollection.items.map((slide: IProductImage) => {
              return slide ? (
                <SwiperSlide key={slide.src}>
                  <ThumbContainer>
                    <ImageMultiply>
                      <Image
                        src={slide.src}
                        alt={slide.title}
                        width={100}
                        height={100}
                        unoptimized={true}
                      />
                    </ImageMultiply>
                  </ThumbContainer>
                </SwiperSlide>
              ) : null;
            })}
            <>
              <div className="nav navPrev">
                <ArrowWrapper>
                  <IconArrowUp width={20} height={20} />
                </ArrowWrapper>
              </div>
              <div className="nav navNext">
                <ArrowWrapper>
                  <IconArrowDown width={20} height={20} />
                </ArrowWrapper>
              </div>
            </>
          </Swiper>
        </ThumbSwiperContainer>
        <SwiperContainer>
          <Swiper
            onSwiper={setSwiper}
            thumbs={{ swiper: thumbsSwiper }}
            style={{ alignItems: 'center' }}
            pagination={{ clickable: true }}>
            {slideCollection.items.map((slide: IProductImage) => {
              return slide ? (
                <SwiperSlide key={slide.src} style={{ textAlign: 'center' }}>
                  <ImgBigContainer>
                    <ReactImageMagnify
                      {...{
                        smallImage: {
                          alt: '',
                          isFluidWidth: true,
                          src: slide.src,
                        },
                        largeImage: {
                          src: slide.src,
                          width: slide.width,
                          height: slide.height,
                        },

                        enlargedImagePosition: 'over',
                        imageClassName: 'imgstyle',
                        enlargedImageContainerClassName: 'enContainer',
                      }}
                    />
                  </ImgBigContainer>
                </SwiperSlide>
              ) : null;
            })}
          </Swiper>
          {show3dicon && (
            <div onClick={openFutureFashion} className="threed">
              <Icon3d />
            </div>
          )}
        </SwiperContainer>
      </Container>
    </SliderWrapper>
  );
};

export default ProductSlider;
